import type { LiveCapabilitiesResponse } from '../types/auth';

/**
 * CORE-774 — the level-up capability diff. After a user levels up, the
 * celebration screen must show ONLY the Live capabilities that actually opened,
 * and those must come from the backend contract — never a level→rights matrix
 * baked into the front end (CORE-769 §0, the hard rule of the whole epic).
 *
 * The set is a DIFF of two effective grants, both read from the server:
 *   opened = fresh `capabilities[]`  −  the last set we saw for this user
 * The "last set we saw" is a per-user high-water persisted through
 * `src/utils/storage.ts` (a flat list, not a matrix) and advanced after the
 * screen is acknowledged, so a repeated `level_up` event lists nothing.
 *
 * This module is deliberately PURE (no React, no storage, no `api`): the diff
 * and its guards are the parts with acceptance criteria, so they are unit-tested
 * directly. The modal owns the persistence and the reactive read.
 *
 * Two hard rules from CORE-769 shape it:
 *  - The level never grants a ROLE-only right. «Проводник света» (the level,
 *    wire-code `master`) must never be presented as the confirmed master role —
 *    role-only keys are filtered out of the diff, and a master (level layer off,
 *    no level) gets an empty diff, never a level-unlock list.
 *  - Rights are only READ. We diff two server-issued lists; we never infer that
 *    a level "should" have opened something.
 */

/**
 * The stable capability catalogue (CORE-768 §6 — 26 keys already persisted in the
 * backend snapshot: the original 24 plus `session.audio.background` and
 * `session.recording.publish_media`, added when the matrix was approved). Every
 * key a level or role can grant lives here, and the copy guard test asserts each
 * has a label so a real unlock is never rendered raw.
 */
export const KNOWN_CAPABILITY_KEYS: readonly string[] = [
  // Создание
  'session.create.instant',
  'session.create.from_dm',
  'session.schedule',
  'session.schedule.recurring',
  'session.library.practice',
  // Практика
  'session.practice.control',
  'session.audio.background',
  // Аудитория
  'session.visibility.private',
  'session.visibility.subscribers',
  'session.visibility.public',
  // Публикация и уведомления
  'session.announce',
  'session.discovery.platform',
  'session.notify.subscribers',
  // Приглашения и вход
  'session.invite.link',
  'session.invite.during_session',
  'session.guest.entry',
  // В сессии (базовые)
  'session.end.manual',
  'session.participants.kick',
  'session.chat.basic',
  // Модерация
  'session.moderation.audio',
  'session.moderation.video',
  'session.moderation.chat',
  'session.cohosts',
  // Статистика / формат
  'session.stats.view',
  'session.format.solo',
  // Запись (роль мастера)
  'session.recording.publish_media',
];

/**
 * Rights the confirmed master ROLE holds and a level never grants (CORE-769 §4,
 * CORE-768 `RoleOnly`). Excluded from the level-up diff so no amount of levelling
 * is ever shown as unlocking them — and so a level is never dressed as a master.
 */
export const ROLE_ONLY_CAPABILITIES: readonly string[] = [
  'session.discovery.platform',
  'session.format.solo',
  'session.stats.view',
  'session.recording.publish_media',
];

/**
 * Human labels for the capability keys — warm, plain, no exclamation (brand
 * voice). This is display COPY for keys the server already granted, NOT a rights
 * matrix: the set of rights still comes from the server; this only names them.
 * The copy guard test keeps every known key labelled.
 */
export const CAPABILITY_LABELS: Readonly<Record<string, string>> = {
  'session.create.instant': 'Мгновенная Live-сессия',
  'session.create.from_dm': 'Создание из диалога',
  'session.schedule': 'Планирование сессий',
  'session.schedule.recurring': 'Повторяющиеся сессии',
  'session.library.practice': 'Практики из библиотеки',
  'session.practice.control': 'Управление воспроизведением',
  'session.audio.background': 'Фоновый трек',
  'session.visibility.private': 'Приватные сессии по ссылке',
  'session.visibility.subscribers': 'Сессии для подписчиков',
  'session.visibility.public': 'Публичные сессии',
  'session.announce': 'Анонс в ленте',
  'session.discovery.platform': 'Показ в событиях платформы',
  'session.notify.subscribers': 'Оповещение подписчиков',
  'session.invite.link': 'Приглашение по ссылке',
  'session.invite.during_session': 'Приглашение во время сессии',
  'session.guest.entry': 'Вход гостей',
  'session.end.manual': 'Завершение сессии',
  'session.participants.kick': 'Удаление участников',
  'session.chat.basic': 'Общий чат',
  'session.moderation.audio': 'Модерация аудио',
  'session.moderation.video': 'Модерация камер',
  'session.moderation.chat': 'Модерация чата',
  'session.cohosts': 'Со-ведущие',
  'session.stats.view': 'Статистика сессий',
  'session.format.solo': 'Формат «Соло»',
  'session.recording.publish_media': 'Публикация записи в «Медиа»',
};

/** Storage key for a user's high-water set of last-seen effective capabilities. */
export function liveCapabilityHighWaterKey(userId: string | null | undefined): string {
  const user = userId == null || userId === '' ? 'anon' : String(userId);
  return `agape_live_caps_highwater_${user}`;
}

/**
 * The capabilities that opened at this level-up: present in the fresh grant, not
 * in the high-water, not role-only, and labelled. Order follows the response so
 * the list reads like the backend's own ordering.
 *
 * Returns [] (→ a soft, non-committal message, never a hardcoded list) when:
 *  - the level layer did not contribute (`levelLayerEnabled` false: flag off,
 *    degraded to baseline, or a master) — this also enforces the level≠role rule;
 *  - the caller is a confirmed master (defence in depth alongside the flag);
 *  - there is no prior high-water yet (first level-up / clean install) — we do
 *    not invent a list;
 *  - nothing actually widened (a repeated event after the high-water advanced).
 */
export function selectNewCapabilities(
  highWater: readonly string[] | null | undefined,
  response: LiveCapabilitiesResponse,
): string[] {
  if (!response.levelLayerEnabled || response.creatorRole === 'master') return [];
  if (highWater == null) return [];

  const previous = new Set(highWater);
  const roleOnly = new Set(ROLE_ONLY_CAPABILITIES);
  return response.capabilities.filter(
    (cap) => !previous.has(cap)
      && !roleOnly.has(cap)
      && CAPABILITY_LABELS[cap] != null,
  );
}

/**
 * CORE-509 — what opens at the stages AHEAD, grouped by the stage that opens it.
 *
 * The open question on the task was «что вообще открывается на уровнях» — and
 * the honest answer the repositories give is: Live-сессии and nothing else.
 * Level is a gate for exactly one contract (`LiveSessionCapabilities`), so this
 * is that same contract read forward instead of backward: the `locked` list the
 * server already sends carries, per capability, the lowest level that would
 * grant it.
 *
 * Nothing is inferred. A capability whose gate is the master ROLE is dropped
 * rather than shown as something to climb toward — the level never becomes the
 * role (CORE-769), and dangling it would be the exact promise this epic forbids.
 *
 * Returns [] when the level layer did not contribute or the caller is a master:
 * there is no ladder to preview if levels are not what decides.
 */
export function selectUpcomingUnlocks(
  response: LiveCapabilitiesResponse,
): Array<{ levelCode: string; capabilities: string[] }> {
  if (!response.levelLayerEnabled || response.creatorRole === 'master') return [];

  const byLevel = new Map<string, string[]>();
  for (const item of response.locked ?? []) {
    if (item.requiresMasterRole) continue;
    const levelCode = item.requiredLevelCode;
    if (!levelCode) continue;
    if (CAPABILITY_LABELS[item.capability] == null) continue;
    const list = byLevel.get(levelCode) ?? [];
    if (!list.includes(item.capability)) list.push(item.capability);
    byLevel.set(levelCode, list);
  }

  return LEVEL_LADDER
    .filter((levelCode) => (byLevel.get(levelCode)?.length ?? 0) > 0)
    .map((levelCode) => ({ levelCode, capabilities: byLevel.get(levelCode)! }));
}

/** Stage order, so «дальше» reads in the order the path is actually walked. */
const LEVEL_LADDER: readonly string[] = ['seeker', 'keeper', 'master', 'grand_master'];
